import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { authService } from '@/domains/internal/auth/services/authService';
import ProfileSettingsModal from '@/domains/internal/user-profile/components/ProfileSettingsModal';
import { Button } from '@/shared/components/ui/Button';

const UserMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const user = authService.getUser();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    // Close dropdown immediately
    setIsOpen(false);

    // Logout and navigate
    await authService.logout();

    // Use window.location for hard redirect to avoid React rendering issues
    window.location.href = '/login';
  };

  const handleProfile = () => {
    setIsOpen(false);
    setIsProfileModalOpen(true);
  };

  if (!user) {
    return null;
  }

  // Type guard to check if user is StaffUser
  const isStaffUser = (u: typeof user): u is import('@/shared/types/api').StaffUser => {
    return u.user_type !== 'referral';
  };

  // Get initials for avatar
  const initials = isStaffUser(user)
    ? `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase()
    : user.name?.charAt(0)?.toUpperCase() || 'R';

  // Get the highest priority role to display
  const getPrimaryRole = () => {
    if (!isStaffUser(user)) return null;

    // Check if user has roles array (new format)
    if (user.roles && user.roles.length > 0) {
      // Priority order: super_admin > admin > doctor > receptionist > technician
      const rolePriority = ['super_admin', 'admin', 'doctor', 'receptionist', 'technician'];
      for (const roleType of rolePriority) {
        if (user.roles.some((r: any) => r.role_type === roleType)) {
          return roleType;
        }
      }
      // If no match, return first role
      return user.roles[0].role_type;
    }
    // Fallback to legacy role field
    return user.role;
  };

  const primaryRole = getPrimaryRole();

  // Get role display name
  const getRoleDisplay = (role: string) => {
    const roleMap: { [key: string]: string } = {
      'super_admin': 'Super Admin',
      'admin': 'Admin',
      'doctor': 'Doctor',
      'receptionist': 'Receptionist',
      'technician': 'Technician'
    };
    return roleMap[role] || role;
  };

  // Get role color
  const getRoleColor = (role: string) => {
    const colorMap: { [key: string]: string } = {
      'super_admin': 'bg-purple-100 text-purple-800',
      'admin': 'bg-blue-100 text-blue-800',
      'doctor': 'bg-green-100 text-green-800',
      'receptionist': 'bg-yellow-100 text-yellow-800',
      'technician': 'bg-gray-100 text-gray-800'
    };
    return colorMap[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Menu Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="neutral"
        padding="px-3 py-2"
        className="flex items-center space-x-3 !bg-transparent hover:!bg-gray-50"
      >
        {/* Avatar */}
        <div className="flex items-center justify-center h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold text-sm">
          {initials}
        </div>

        {/* User Info - Hidden on small screens */}
        <div className="hidden md:flex flex-col items-start">
          <span className="text-sm font-medium text-gray-900">
            {isStaffUser(user) ? `${user.first_name} ${user.last_name}` : user.name}
          </span>
          {primaryRole && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleColor(primaryRole)}`}>
              {getRoleDisplay(primaryRole)}
            </span>
          )}
        </div>

        {/* Chevron */}
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold">
                {initials}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  {isStaffUser(user) ? `${user.first_name} ${user.last_name}` : user.name}
                </p>
                <p className="text-xs text-gray-500">{user.email}</p>
                {primaryRole && (
                  <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${getRoleColor(primaryRole)}`}>
                    {getRoleDisplay(primaryRole)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <Button
              onClick={handleProfile}
              variant="neutral"
              fullWidth
              padding="px-4 py-2"
              fontSize="text-sm"
              className="flex items-center !bg-transparent !text-gray-700 hover:!bg-gray-50 !justify-start"
            >
              <Cog6ToothIcon className="h-5 w-5 mr-3 text-gray-400" />
              Profile Settings
            </Button>

            <Button
              onClick={() => {
                setIsOpen(false);
                navigate('/my-time-off');
              }}
              variant="neutral"
              fullWidth
              padding="px-4 py-2"
              fontSize="text-sm"
              className="flex items-center !bg-transparent !text-gray-700 hover:!bg-gray-50 !justify-start"
            >
              <CalendarIcon className="h-5 w-5 mr-3 text-gray-400" />
              My Time Off
            </Button>

            <Button
              onClick={handleLogout}
              variant="neutral"
              fullWidth
              padding="px-4 py-2"
              fontSize="text-sm"
              className="flex items-center !bg-transparent hover:!bg-red-50 !justify-start"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" style={{ color: '#dc2626' }} />
              <span style={{ color: '#dc2626' }}>Sign Out</span>
            </Button>
          </div>
        </div>
      )}

      {/* Profile Settings Modal */}
      <ProfileSettingsModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />
    </div>
  );
};

export default UserMenu;
